import { useState } from "react";
import { RefreshCw, Save, X, ChevronDown } from "lucide-react";
import { SpecimenIcon } from "../../lib/icons";
import { IconPicker } from "./IconPicker";
import { VoiceControls } from "./VoiceControls";
import { MemoryFields } from "./MemoryFields";
import { RiddleFields } from "./RiddleFields";

const SOURCE = { ai: ["text-cyan-300", "AI generated"], fallback: ["text-amber-300", "Generator fallback"], saved: ["text-slate-400", "Saved entry"] };

const Field = ({ label, children }) => (
  <div>
    <span className="lab-label">{label}</span>
    <div className="mt-1">{children}</div>
  </div>
);

export const BenchReview = ({ draft, setDraft, name, setName, onRegenerate, onSave, onCancel, busy, isUpdate }) => {
  const [pickIcon, setPickIcon] = useState(false);
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const [srcColor, srcLabel] = SOURCE[draft.source] || SOURCE.saved;
  const onKey = (e) => {
    if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); onSave(); }
    if (e.key === "Escape") onCancel();
  };

  return (
    <section data-testid="bench-review" onKeyDown={onKey} className="border border-emerald-400/50 bg-slate-950/80 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="lab-label !text-emerald-300">Step 3 // The memory inside</p>
        <div className="flex items-center gap-3">
          <span data-testid="bench-source" className={`font-mono text-[10px] uppercase tracking-widest ${srcColor}`}>{srcLabel}</span>
          <button type="button" data-testid="bench-save-top-button" onClick={onSave} disabled={busy} className="lab-btn-primary py-1.5">
            <Save size={14} /> {isUpdate ? "Update" : "Save"} &amp; next
          </button>
        </div>
      </div>
      <div className="mt-4 grid gap-6 md:grid-cols-[150px_1fr]">
        <div>
          <button type="button" data-testid="bench-icon-button" onClick={() => setPickIcon(!pickIcon)} title="Change icon" className="flash-icon-frame flex h-[150px] w-[150px] items-center justify-center text-emerald-300">
            <SpecimenIcon name={draft.icon} size={84} strokeWidth={1.4} />
          </button>
          <p className="mt-2 flex items-center justify-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{draft.icon} <ChevronDown size={12} /></p>
        </div>
        <div className="grid gap-3">
          <Field label="Object"><input data-testid="bench-review-name" value={name} onChange={(e) => setName(e.target.value)} className="lab-input w-full" /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Hive word">
              <input data-testid="bench-review-word" autoFocus value={draft.spore_word} onChange={set("spore_word")} className="lab-input w-full font-mono text-xl uppercase tracking-[0.2em] text-cyan-300" />
            </Field>
            <Field label="Sound it makes">
              <input data-testid="bench-review-sound" value={draft.sound_word} onChange={set("sound_word")} className="lab-input w-full font-mono text-xl text-emerald-300" />
            </Field>
          </div>
          <Field label="Translation (hive meaning)"><input data-testid="bench-review-meaning" value={draft.meaning} onChange={set("meaning")} className="lab-input w-full font-mono" /></Field>
          <VoiceControls voice={draft.voice} onChange={(voice) => setDraft({ ...draft, voice })} soundWord={draft.sound_word} word={draft.spore_word} />
          <MemoryFields memory={draft.memory} sfxPrompt={draft.sfx_prompt || ""} onChange={(patch) => setDraft({ ...draft, ...patch })} idPrefix="bench-memory" />
          <RiddleFields riddle={draft.riddle} name={name} memory={draft.memory} onChange={(patch) => setDraft({ ...draft, ...patch })} idPrefix="bench-riddle" />
        </div>
      </div>
      {pickIcon && <div className="mt-4"><IconPicker value={draft.icon} onChange={(icon) => { setDraft({ ...draft, icon }); setPickIcon(false); }} /></div>}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button type="button" data-testid="bench-regenerate-button" onClick={onRegenerate} disabled={busy} className="lab-btn-ghost">
          <RefreshCw size={16} className={busy ? "animate-spin" : ""} /> {busy ? "Asking again..." : "Regenerate"}
        </button>
        <div className="flex gap-3">
          <button type="button" data-testid="bench-cancel-button" onClick={onCancel} className="lab-btn-ghost"><X size={16} /> Cancel (Esc)</button>
          <button type="button" data-testid="bench-save-button" onClick={onSave} disabled={busy} className="lab-btn-primary">
            <Save size={16} /> {isUpdate ? "Update" : "Save"} &amp; next (Enter)
          </button>
        </div>
      </div>
    </section>
  );
};
